import React, { useEffect, useState } from "react";
import { Loader2, Eye, Search, BookOpen, MessageCircle, ArrowUpRight, ShieldCheck, Monitor, Smartphone, Tablet } from "lucide-react";
import { adminApi } from "../../lib/api";

const PERIODS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const Card = ({ title, subtitle, children, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid={testid}>
    <h3 className="font-display font-bold text-navy">{title}</h3>
    {subtitle && <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="rounded-xl bg-slate-50 p-4">
    <div className="flex items-center gap-1.5 text-slate-400">
      <Icon size={14} />
      <span className="text-xs">{label}</span>
    </div>
    <p className="text-2xl font-display font-bold text-navy mt-1.5">{value}</p>
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const Trend = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.views));
  return (
    <div className="flex items-end gap-[3px] h-28" data-testid="ask-stats-trend">
      {daily.map((d) => (
        <div key={d.date} className="flex-1 group relative flex flex-col justify-end h-full">
          <div
            className="w-full rounded-t bg-navy/80 hover:bg-orange transition-colors min-h-[2px]"
            style={{ height: `${(d.views / max) * 100}%` }}
          />
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-lg bg-navy text-white text-[11px] px-2 py-1 z-10">
            {d.date} · {d.views} view{d.views === 1 ? "" : "s"} · {d.visits} visit{d.visits === 1 ? "" : "s"}
          </span>
        </div>
      ))}
    </div>
  );
};

const Empty = ({ children }) => <p className="text-sm text-slate-400">{children}</p>;

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
          {head.map((h) => (
            <th key={h} className="pb-2 font-bold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

export default function AskCassieStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.askCassie.stats(days).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days]);

  if (loading && !data) return <p className="text-sm text-slate-400">Loading…</p>;
  if (!data) return <p className="text-sm text-red-500">Couldn't load the visitor figures.</p>;

  const t = data.totals;
  const visits = Math.max(1, t.visits);

  return (
    <div data-testid="ask-cassie-stats">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              data-testid={`ask-stats-period-${p.days}`}
              className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors ${
                days === p.days ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-orange"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {loading && <Loader2 size={15} className="animate-spin text-slate-400" />}
      </div>

      <Card
        title={`Visitors to Ask Cassie — last ${data.days} days`}
        subtitle="Counted on our own server, with no cookie and no personal data: a random id per browser tab tells visits apart, and the whole lot is deleted after 90 days with only anonymous monthly totals kept."
        testid="ask-stats-summary"
      >
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat icon={Eye} label="Page views" value={t.views} />
          <Stat icon={ShieldCheck} label="Visits" value={t.visits} hint="separate browser tabs" />
          <Stat icon={Search} label="Searches" value={t.searches} hint={`${t.zero_searches} found nothing`} />
          <Stat icon={BookOpen} label="Answers read" value={t.opens} hint={`${t.answers_read} different answers`} />
          <Stat icon={MessageCircle} label="Asked Cassie" value={t.chat_questions} hint={`${t.chat_no_answer} with no library match`} />
          <Stat icon={ArrowUpRight} label="Clicks to talk" value={t.outbound} hint={`${Math.round((t.outbound / visits) * 100)}% of visits`} />
        </div>

        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-wide font-bold text-slate-400 mb-2">Page views per day</p>
          <Trend daily={data.daily} />
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="ask-stats-devices">
          {data.devices.map((d) => {
            const Icon = DEVICE_ICON[d.device] || Monitor;
            return (
              <span key={d.device} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-navy/5 text-navy px-3 py-1.5 rounded-full">
                <Icon size={13} /> {d.device} · {d.visits} visit{d.visits === 1 ? "" : "s"}
              </span>
            );
          })}
          {data.tracked_since && (
            <span className="text-[11px] text-slate-400 self-center">Tracking began {String(data.tracked_since).slice(0, 10)}</span>
          )}
        </div>
      </Card>

      <Card title="Which pages they visited" subtitle="The hub, each subject and each answer page." testid="ask-stats-pages-card">
        {data.pages.length === 0 ? (
          <Empty>No page views in this period yet.</Empty>
        ) : (
          <Table
            testid="ask-stats-pages"
            head={["Page", "Type", "Views", "Visits"]}
            rows={data.pages.map((p) => (
              <tr key={p.path} className="border-t border-slate-100">
                <td className="py-2.5 pr-4">
                  <a href={p.path} target="_blank" rel="noreferrer" className="text-navy hover:text-orange font-semibold">{p.label}</a>
                  <span className="block text-[11px] text-slate-400">{p.path}</span>
                </td>
                <td className="py-2.5 pr-4 text-slate-500 capitalize">{p.kind}</td>
                <td className="py-2.5 pr-4 font-semibold text-navy">{p.views}</td>
                <td className="py-2.5 text-slate-500">{p.visits}</td>
              </tr>
            ))}
          />
        )}
      </Card>

      <Card
        title="What they searched for"
        subtitle="Every term typed into the hub search, with how many answers it returned. A zero means a visitor left empty-handed — that's the next answer to write."
        testid="ask-stats-searches-card"
      >
        {data.searches.length === 0 ? (
          <Empty>No searches recorded in this period.</Empty>
        ) : (
          <Table
            testid="ask-stats-searches"
            head={["Search", "Times", "Answers returned", "Last searched"]}
            rows={data.searches.map((s) => (
              <tr key={s.term} className="border-t border-slate-100">
                <td className="py-2.5 pr-4 text-navy font-semibold">{s.term}</td>
                <td className="py-2.5 pr-4 text-slate-500">{s.count}</td>
                <td className="py-2.5 pr-4">
                  {s.zero ? (
                    <span className="text-xs font-bold text-amber-600 bg-amber-50 rounded-full px-2.5 py-1">found nothing</span>
                  ) : (
                    <span className="text-slate-500">{s.avg_results ?? "—"}</span>
                  )}
                </td>
                <td className="py-2.5 text-slate-400 text-xs">{String(s.last_seen || "").slice(0, 10)}</td>
              </tr>
            ))}
          />
        )}
      </Card>

      <Card
        title="What they read"
        subtitle="Answers opened in this period, and the published answers nobody opened at all — worth rewording, merging or promoting."
        testid="ask-stats-read-card"
      >
        {data.read.top.length === 0 ? (
          <Empty>No answers opened in this period.</Empty>
        ) : (
          <Table
            testid="ask-stats-read"
            head={["Answer", "Subject", "Opened"]}
            rows={data.read.top.map((r) => (
              <tr key={r.slug} className="border-t border-slate-100">
                <td className="py-2.5 pr-4 text-navy font-semibold">{r.question}</td>
                <td className="py-2.5 pr-4 text-slate-500">{r.category_name}</td>
                <td className="py-2.5 font-semibold text-navy">{r.opens}</td>
              </tr>
            ))}
          />
        )}

        <details className="mt-5" data-testid="ask-stats-never-read">
          <summary className="text-sm font-semibold text-navy cursor-pointer">
            {data.read.never_count} published answer{data.read.never_count === 1 ? "" : "s"} nobody opened
          </summary>
          <ul className="mt-3 space-y-1.5">
            {data.read.never.map((r) => (
              <li key={r.slug} className="text-xs text-slate-500">
                <span className="text-navy font-semibold">{r.question}</span>
                {r.category_name ? ` · ${r.category_name}` : ""}
              </li>
            ))}
          </ul>
          {data.read.never_count > data.read.never.length && (
            <p className="text-[11px] text-slate-400 mt-2">Showing the first {data.read.never.length}.</p>
          )}
        </details>
      </Card>

      <Card
        title="What they asked Cassie from these pages"
        subtitle={`${t.chat_questions} question${t.chat_questions === 1 ? "" : "s"} came from an Ask Cassie page (${t.chat_questions_site} across the whole site). ${t.chat_no_answer} had no library answer behind them and ${t.chat_handovers} asked to speak to the team.`}
        testid="ask-stats-chat-card"
      >
        {data.chat.questions.length === 0 ? (
          <Empty>No chat questions from these pages in this period.</Empty>
        ) : (
          <Table
            testid="ask-stats-chat"
            head={["Question", "Times asked"]}
            rows={data.chat.questions.map((c) => (
              <tr key={c.question} className="border-t border-slate-100">
                <td className="py-2.5 pr-4 text-navy">{c.question}</td>
                <td className="py-2.5 text-slate-500">{c.count}</td>
              </tr>
            ))}
          />
        )}
      </Card>

      <Card
        title="Where they went next"
        subtitle="Clicks from an Ask Cassie page towards talking to CASS — the chatbot, a service page or the call to action on an answer."
        testid="ask-stats-outbound-card"
      >
        {data.outbound.length === 0 ? (
          <Empty>No onward clicks recorded in this period.</Empty>
        ) : (
          <Table
            testid="ask-stats-outbound"
            head={["Destination", "Clicks"]}
            rows={data.outbound.map((o) => (
              <tr key={o.target} className="border-t border-slate-100">
                <td className="py-2.5 pr-4 text-navy font-semibold">{o.target}</td>
                <td className="py-2.5 text-slate-500">{o.clicks}</td>
              </tr>
            ))}
          />
        )}
      </Card>

      {data.history.length > 0 && (
        <Card
          title="Monthly history"
          subtitle="What survives the 90-day deletion: anonymous counts only, kept so you can compare one year with the next."
          testid="ask-stats-history-card"
        >
          <Table
            testid="ask-stats-history"
            head={["Month", "Visits", "Views", "Searches", "Answers opened", "Chat messages"]}
            rows={data.history.map((h) => (
              <tr key={h.period} className="border-t border-slate-100">
                <td className="py-2.5 pr-4 text-navy font-semibold">{h.period}</td>
                <td className="py-2.5 pr-4 text-slate-500">{h.hub_visits ?? "—"}</td>
                <td className="py-2.5 pr-4 text-slate-500">{h.hub_views ?? "—"}</td>
                <td className="py-2.5 pr-4 text-slate-500">{h.hub_searches ?? "—"}</td>
                <td className="py-2.5 pr-4 text-slate-500">{h.hub_opens ?? "—"}</td>
                <td className="py-2.5 text-slate-500">{h.chat_messages ?? "—"}</td>
              </tr>
            ))}
          />
        </Card>
      )}
    </div>
  );
}
